import { useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { useFlashUnstakeMutation } from 'modules/liquidStaking/actions/flashUnstake';
import { useUnstakeMutation } from 'modules/liquidStaking/actions/unstake';
import { DEFAULT_UNSTAKE_TYPE, UnstakeType } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';

interface IUseUnstakeActionResult {
  unstake: (amount: BigNumber) => Promise<unknown>;
  isLoading: boolean;
  reset: () => void;
  txHash?: Address;
  type: UnstakeType;
  setType: (type: UnstakeType) => void;
}

export function useUnstakeAction(): IUseUnstakeActionResult {
  const { poolAddress, isDeprecated } = useLiquidStakingPool();
  const [type, setType] = useState(
    isDeprecated ? UnstakeType.Unstake : DEFAULT_UNSTAKE_TYPE,
  );
  const [
    unstake,
    { isLoading: isUnstakeLoading, data: unstakeData, reset: resetUnstake },
  ] = useUnstakeMutation();
  const [
    flashUnstake,
    {
      isLoading: isFlashUnstakeLoading,
      data: flashUnstakeData,
      reset: resetFlashUnstake,
    },
  ] = useFlashUnstakeMutation();

  const {
    executeUnstake,
    isLoading,
    reset,
    data: txHash,
  } = useMemo(() => {
    switch (type) {
      case UnstakeType.FlashUnstake:
        return {
          executeUnstake: (amount: BigNumber) =>
            flashUnstake({ amount, poolAddress }),
          isLoading: isFlashUnstakeLoading,
          data: flashUnstakeData,
          reset: resetFlashUnstake,
        };
      case UnstakeType.Unstake:
      default:
        return {
          executeUnstake: (amount: BigNumber) =>
            unstake({ amount, poolAddress }),
          isLoading: isUnstakeLoading,
          data: unstakeData,
          reset: resetUnstake,
        };
    }
  }, [
    poolAddress,
    flashUnstake,
    flashUnstakeData,
    isFlashUnstakeLoading,
    isUnstakeLoading,
    resetFlashUnstake,
    resetUnstake,
    type,
    unstake,
    unstakeData,
  ]);

  return {
    unstake: executeUnstake,
    isLoading,
    reset,
    txHash,
    type,
    setType,
  };
}
